import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuthenticationErrorMessage, login } from '../../../api/authApi';
import { useAppDispatch } from '../../../store/hooks';
import { loginFailure, loginStart, loginSuccess } from '../../../store/authSlice';
import { tokenHasPermission } from '../utils/jwtPermissions';
import { persistAuthentication } from '../services/authSession';
import './LoginPage.css';


const LoginPage: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!identifier.trim() || !password.trim()) {
      return;
    }

    setLoading(true);
    setError('');
    dispatch(loginStart());

    try {
      const response = await login({
        username: identifier.trim(),
        password,
      });

      const token = response.result?.token;
      const refreshToken = response.result?.refreshToken;
      const isSuccessCode = ['0', '200'].includes(String(response.code));

      if (!isSuccessCode || !token || !refreshToken) {
        throw new Error(response.message || 'Đăng nhập không thành công.');
      }

      const session = persistAuthentication(response.result);
      dispatch(loginSuccess(session));
      const requestedPath = typeof location.state?.from === 'string' ? location.state.from : null;
      navigate(
        requestedPath || (tokenHasPermission(token, 'USER_READ_ALL') ? '/admin' : '/'),
        { replace: true },
      );
    } catch (submitError) {
      const message = getAuthenticationErrorMessage(
        submitError,
        'Có lỗi xảy ra. Vui lòng thử lại.',
      );
      setError(message);
      dispatch(loginFailure(message));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // TODO: Tích hợp Google OAuth
    console.log('Google Login');
  };

  const handleAppleLogin = () => {
    // TODO: Tích hợp Apple OAuth
    console.log('Apple Login');
  };

  return (
    <main className="login-page">
      {/* Decoration background */}
      <div
        className="login-decoration login-decoration-left"
        aria-hidden="true"
      />

      <div
        className="login-decoration login-decoration-right"
        aria-hidden="true"
      />

      <section className="login-container">
        {/* Header */}
        <header className="login-header">
          <h1>Chào mừng trở lại</h1>

          <p>
            Đăng nhập để quản lý đơn hàng, bảo hành và nhận ưu đãi từ
            Phương Đông.
          </p>
        </header>

        {/* Login Card */}
        <div className="login-card">
          <form className="login-form" onSubmit={handleSubmit}>
            {/* Email / Phone / Username */}
            <div className="login-field">
              <label htmlFor="identifier">
                Email, số điện thoại hoặc Username
                <span className="required">*</span>
              </label>

              <div className="login-input-wrapper">
                <input
                  id="identifier"
                  name="identifier"
                  type="text"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  placeholder="Nhập email, số điện thoại hoặc username"
                  autoComplete="username"
                />

                <span className="input-icon" aria-hidden="true">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21a8 8 0 0 1 16 0" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Password */}
            <div className="login-field">
              <div className="password-label-row">
                <label htmlFor="password">
                  Mật khẩu
                  <span className="required">*</span>
                </label>

                <a
                  href="/forgot-password"
                  className="forgot-password"
                >
                  Quên mật khẩu?
                </a>
              </div>

              <div className="login-input-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Nhập mật khẩu"
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={
                    showPassword
                      ? 'Ẩn mật khẩu'
                      : 'Hiện mật khẩu'
                  }
                >
                  {showPassword ? (
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 3l18 18" />
                      <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                      <path d="M9.9 4.2A10.6 10.6 0 0 1 12 4c5.5 0 9.5 4.5 10 8a10.7 10.7 0 0 1-2.1 4.2" />
                      <path d="M6.6 6.6C4.1 8.1 2.4 10.4 2 12c.8 3.5 4.7 8 10 8 1.2 0 2.3-.2 3.4-.6" />
                    </svg>
                  ) : (
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && <div className="login-error" role="alert">{error}</div>}

            {/* Login Button */}
            <button
              type="submit"
              className="continue-button"
              disabled={
                loading ||
                !identifier.trim() ||
                !password.trim()
              }
            >
              {loading ? 'ĐANG ĐĂNG NHẬP...' : 'ĐĂNG NHẬP'}
            </button>

            {/* Divider */}
            <div className="login-divider">
              <span>hoặc</span>
            </div>

            {/* Google */}
            <button
              type="button"
              className="social-login-button"
              onClick={handleGoogleLogin}
            >
              <span className="google-icon">
                G
              </span>

              <span>
                Tiếp tục với Google
              </span>
            </button>

            {/* Apple */}
            <button
              type="button"
              className="social-login-button"
              onClick={handleAppleLogin}
            >
              <span className="apple-icon">
                
              </span>

              <span>
                Tiếp tục với Apple
              </span>
            </button>

            {/* Register */}
            <div className="register-section">
              <span>Chưa có tài khoản?</span>{' '}

              <a href="/register">
                Đăng ký ngay
              </a>
            </div>
          </form>
        </div>

        {/* Policy */}
        <div className="login-footer">
          <p>
            Bằng việc tiếp tục, bạn đồng ý với{' '}
            <a href="/terms">
              Điều khoản sử dụng
            </a>{' '}
            và{' '}
            <a href="/privacy-policy">
              Chính sách bảo mật
            </a>{' '}
            của Phương Đông.
          </p>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
